import { nullableOptional } from '@shigo-ui/api-client';
import { z } from 'zod';

export enum DebugEventType {
  RawRequest = 'Raw Request',
  Request = 'Request',
  DpRequest = 'DP Request',
  Unknown = 'Unknown',
}

export enum DebugEventTypeRequest {
  RawRequest = 'RAW_REQ',
  Request = 'REQ',
  DpRequest = 'DP_REQ',
}

export enum DebugStatus {
  Running = 'RUNNING',
  Completed = 'COMPLETED',
  Canceled = 'CANCELED',
  Failed = 'FAILED',
  Unknown = 'UNKNOWN',
}

export enum DebugSourceType {
  Publisher = 'PUBLISHER',
  Placement = 'PLACEMENT',
  DemandPartner = 'DEMAND_PARTNER',
  Unknown = 'UNKNOWN',
}

export enum DebugSortProperty {
  Status = 'status',
  Enabled = 'enabled',
  EnabledUntil = 'enabledUntil',
  SampleRate = 'sampleRate',
  EnabledBy = 'enabledBy',
}

export enum DebugSortDirection {
  Asc = 'ASC',
  Desc = 'DESC',
}

export enum DebugLookupStatus {
  Active = 'ACTIVE',
  Inactive = 'INACTIVE',
}

const eventTypeRequestToDisplay: Record<DebugEventTypeRequest, DebugEventType> = {
  [DebugEventTypeRequest.RawRequest]: DebugEventType.RawRequest,
  [DebugEventTypeRequest.Request]: DebugEventType.Request,
  [DebugEventTypeRequest.DpRequest]: DebugEventType.DpRequest,
};

export const debugRuleEventTypes = [
  DebugEventType.RawRequest,
  DebugEventType.Request,
  DebugEventType.DpRequest,
] as const;

export const debugEventTypeRequestMap: Record<
  (typeof debugRuleEventTypes)[number],
  DebugEventTypeRequest
> = {
  [DebugEventType.RawRequest]: DebugEventTypeRequest.RawRequest,
  [DebugEventType.Request]: DebugEventTypeRequest.Request,
  [DebugEventType.DpRequest]: DebugEventTypeRequest.DpRequest,
};

export const debugEventTypeSchema = z.preprocess((value) => {
  const normalized =
    typeof value === 'string' && value in eventTypeRequestToDisplay
      ? eventTypeRequestToDisplay[value as DebugEventTypeRequest]
      : value;
  return enumValueOrUnknown(DebugEventType, normalized, DebugEventType.Unknown);
}, z.enum(DebugEventType));

export const debugStatusSchema = z.preprocess(
  (value) => enumValueOrUnknown(DebugStatus, value, DebugStatus.Unknown),
  z.enum(DebugStatus),
);

export const debugSourceTypeSchema = z.preprocess(
  (value) => enumValueOrUnknown(DebugSourceType, value, DebugSourceType.Unknown),
  z.enum(DebugSourceType),
);

const debugEnabledBySchema = z
  .union([
    z.string(),
    z.looseObject({
      name: nullableOptional(z.string()),
      email: nullableOptional(z.string()),
      username: nullableOptional(z.string()),
    }),
    z.null(),
  ])
  .transform((value) => {
    if (typeof value === 'string') return value;
    return value?.name ?? value?.email ?? value?.username ?? undefined;
  });

export const debugSourceSchema = z.looseObject({
  sourceType: debugSourceTypeSchema,
  id: nullableOptional(z.coerce.string()),
  secondaryId: nullableOptional(z.coerce.string()),
  name: nullableOptional(z.string()),
});

export const debugRuleSchema = z.looseObject({
  jobId: z.coerce.string(),
  networkId: nullableOptional(z.number()),
  enabled: z.boolean(),
  status: debugStatusSchema,
  enabledAt: nullableOptional(z.string()),
  enabledUntil: nullableOptional(z.string()),
  sampleRate: nullableOptional(z.number()),
  eventTypes: z.array(debugEventTypeSchema).default([]),
  enabledBy: debugEnabledBySchema.optional(),
  sources: z.array(debugSourceSchema).default([]),
});

export const debugRulesPageSchema = z.looseObject({
  content: z.array(debugRuleSchema),
  totalElements: z.number().int().nonnegative(),
  totalPages: z.number().int().nonnegative().optional(),
  number: z.number().int().nonnegative().optional(),
  size: z.number().int().positive().optional(),
});

export const debugLookupPageSchema = z.looseObject({
  content: z.array(
    z.looseObject({
      id: z.coerce.string(),
      name: nullableOptional(z.string()),
      displayName: nullableOptional(z.string()),
      secondaryId: nullableOptional(z.coerce.string()),
    }),
  ),
  totalElements: z.number().int().nonnegative().optional(),
});

export type DebugRule = z.infer<typeof debugRuleSchema>;
export type DebugSource = z.infer<typeof debugSourceSchema>;
export type DebugLookupOption = z.infer<typeof debugLookupPageSchema>['content'][number];

export interface DebugRuleRequest {
  publisherSecondaryId?: string;
  placementSecondaryId?: string;
  demandPartnerId?: string;
  sampleRate?: number;
  durationSeconds?: number;
  eventTypes: DebugEventTypeRequest[];
  enabled: boolean;
}

export interface DebugListFilter {
  searchTerm: string;
  page: number;
  size: number;
  sortProperty: DebugSortProperty | null;
  sortDirection: DebugSortDirection;
}

export const defaultDebugListFilter: DebugListFilter = {
  searchTerm: '',
  page: 0,
  size: 20,
  sortProperty: DebugSortProperty.EnabledUntil,
  sortDirection: DebugSortDirection.Desc,
};

export const debugPageSizes = [10, 20, 50, 100] as const;

export function isDebugSortProperty(value: string): value is DebugSortProperty {
  return Object.values(DebugSortProperty).includes(value as DebugSortProperty);
}

export function debugLookupLabel(option: DebugLookupOption) {
  return option.name ?? option.displayName ?? option.secondaryId ?? option.id;
}

export function isDebugRuleEventType(
  value: DebugEventType,
): value is (typeof debugRuleEventTypes)[number] {
  return debugRuleEventTypes.includes(value as (typeof debugRuleEventTypes)[number]);
}

function enumValueOrUnknown<TEnum extends Record<string, string>>(
  enumValue: TEnum,
  value: unknown,
  unknownValue: TEnum[keyof TEnum],
) {
  return typeof value === 'string' && Object.values(enumValue).includes(value)
    ? value
    : unknownValue;
}
